/**
 * rememberService — the web half of f8's memory limits (PPL-350).
 *
 * Port of the app's `src/services/rememberService.ts`, sharing its contract
 * knowledge verbatim; if a rule below surprises you, the app file carries the
 * same rule with the same reasoning.
 *
 * Two fences on one noun. **Breadth**: how many ppl f8 remembers — 5 on free,
 * everyone on premium. **Permanence**: how far back it remembers them.
 *
 * ## Nothing here is a number this file knows
 *
 * Every limit and count is SERVED. Screens read `limit`, `remaining`,
 * `memoryWindowDays` and the archive counts off these callables and never
 * hard-code 5 or 30 — both are Firestore config that changes without a release,
 * and the copy has to read correctly at three, at ten, and at not-yet.
 *
 * ## `memoryWindowDays: null` is a real state, not a missing value
 *
 * Null means no window is in force: premium, and ALSO everyone while
 * `rememberedGate` is still off — these surfaces ship before the fence is
 * switched on. Every window string and the whole archive row must render NOTHING
 * in that case. While the gate is off those memories are not dormant, so
 * "invisible to f8" would be false and a zero would be a number nobody measured.
 *
 * The slot counter is not conditional the same way — the server enforces the
 * people limit today regardless of the gate — so `limit`/`remaining` are live now.
 *
 * ## Copy contract
 *
 * "f8 remembers", never "track". Archived / preserved / paused, never lost or
 * deleted — nothing in this feature removes anything.
 */

import {
  getMemoryArchiveEurW,
  getRememberedPeopleQuotaEurW,
  setPersonRememberedEurW,
} from '../firebaseConfig';

/** What the slot counter and every window string read from. */
export interface RememberQuota {
  isPremium: boolean;
  /** Null when unlimited. */
  limit: number | null;
  /** People f8 remembers — the metered number. */
  activeCount: number;
  /** People f8 holds a model of, paused included. Never decreases on a lapse. */
  rememberedCount: number;
  /** Null when unlimited. */
  remaining: number | null;
  /** Null when NO window is in force — premium, or the fence not yet switched on. */
  memoryWindowDays: number | null;
  /**
   * The free plan's numbers, served to EVERYONE (PPL-383) — an entitled user's
   * `limit`/`memoryWindowDays` are null precisely when the trial UX needs to
   * name what day 14 takes away. `freeMemoryWindowDays` keeps the same
   * absence-means-say-nothing contract as `memoryWindowDays`.
   *
   * The callable also echoes `isTrialing`/`trialEndsAt`; they are deliberately
   * NOT surfaced here — the chrome's countdown derives from the user doc via
   * `trialCountdownOf`, and a second, subtly different "is trialing" on this
   * type is a trap for the next surface that reaches for it.
   */
  freeLimit: number | null;
  freeMemoryWindowDays: number | null;
}

/** One person's memories, split at the window edge. `null` = not established. */
export interface PersonArchive {
  kept: number | null;
  archived: number | null;
  total: number | null;
  paused: boolean;
  memoryWindowDays: number | null;
}

/** Everything an upgrade would bring back, across the account. */
export interface AccountArchive {
  archived: number | null;
  people: number;
  /** The fan-out cap stopped the walk — say "and counting". */
  truncated: boolean;
  memoryWindowDays: number | null;
}

/** The server's refusal when a free user reaches for a slot they don't have. */
export interface RememberLimitError {
  limit: number | null;
  activeCount: number;
  rememberedCount: number;
}

/**
 * Is this the at-limit refusal, as opposed to any other failure?
 *
 * Matched on the server's `reason`, never the message: the message is copy and
 * will change, and the one thing that must not happen is a network blip being
 * mistaken for a paywall and answered with an upgrade sheet.
 */
export function asRememberLimitError(error: unknown): RememberLimitError | null {
  const e = error as { code?: string; details?: Record<string, unknown> } | null;
  if (!e || e.code !== 'functions/resource-exhausted') return null;
  const d = e.details;
  if (!d || d.reason !== 'remembered_people_limit') return null;
  return {
    limit: typeof d.limit === 'number' ? d.limit : null,
    activeCount: typeof d.activeCount === 'number' ? d.activeCount : 0,
    rememberedCount: typeof d.rememberedCount === 'number' ? d.rememberedCount : 0,
  };
}

/** Server-JSON number coercers, shared with trialService — one contract. */
export const numOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;
export const numOr0 = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

function toQuota(d: Record<string, unknown>, windowDays: number | null): RememberQuota {
  return {
    isPremium: d.isPremium === true,
    limit: numOrNull(d.limit),
    activeCount: numOr0(d.activeCount),
    rememberedCount: numOr0(d.rememberedCount),
    remaining: numOrNull(d.remaining),
    memoryWindowDays: windowDays,
    freeLimit: numOrNull(d.freeLimit),
    freeMemoryWindowDays: numOrNull(d.freeMemoryWindowDays),
  };
}

/** The caller's quota. Throws — callers decide whether a failure hides the counter. */
export async function fetchRememberQuota(): Promise<RememberQuota> {
  const res = await getRememberedPeopleQuotaEurW({});
  const d = (res.data || {}) as Record<string, unknown>;
  return toQuota(d, numOrNull(d.memoryWindowDays));
}

/**
 * Turn remembering on or off for one person.
 *
 * Off PAUSES rather than forgets: the timeline, extractions and links all stay,
 * so re-adding is instant and nothing the user built is destroyed. The UI must
 * never describe this as deleting.
 *
 * Throws the raw callable error so the caller can run it through
 * {@link asRememberLimitError} and sell to that one case only.
 */
export async function setPersonRemembered(
  personId: string,
  remembered: boolean,
): Promise<RememberQuota> {
  const res = await setPersonRememberedEurW({ personId, remembered });
  // This callable does not serve the window — the caller keeps whatever it had
  // rather than clearing a row it cannot re-derive.
  return toQuota((res.data || {}) as Record<string, unknown>, null);
}

/** One person's split across the window edge, for the archive row on their profile. */
export async function fetchPersonArchive(personId: string): Promise<PersonArchive> {
  const res = await getMemoryArchiveEurW({ personId });
  const d = (res.data || {}) as Record<string, unknown>;
  return {
    kept: numOrNull(d.kept),
    archived: numOrNull(d.archived),
    total: numOrNull(d.total),
    paused: d.paused === true,
    memoryWindowDays: numOrNull(d.memoryWindowDays),
  };
}

/** The account-wide total the at-limit sheet sells against. */
export async function fetchAccountArchive(): Promise<AccountArchive> {
  const res = await getMemoryArchiveEurW({});
  const d = (res.data || {}) as Record<string, unknown>;
  return {
    archived: numOrNull(d.archived),
    people: numOr0(d.people),
    truncated: d.truncated === true,
    memoryWindowDays: numOrNull(d.memoryWindowDays),
  };
}

/* ------------------------------------------------------------------ *
 * Presentation helpers — one place, so two pages cannot disagree
 * ------------------------------------------------------------------ */

export type RememberState = 'active' | 'paused' | 'never';

export function rememberStateOf(person: unknown): RememberState {
  const p = (person || {}) as Record<string, unknown>;
  if (p.remembered !== true) return 'never';
  return p.pausedAt ? 'paused' : 'active';
}

/**
 * The slot counter's words, or null for "render no counter".
 *
 * Premium gets nothing at all: a limit you cannot reach is not reassurance, it is
 * a standing reminder that a limit exists.
 */
export function slotLabel(
  quota: Pick<RememberQuota, 'isPremium' | 'limit' | 'activeCount'>,
): string | null {
  if (quota.isPremium || quota.limit === null) return null;
  const remaining = Math.max(0, quota.limit - quota.activeCount);
  if (remaining === 0) return 'no slots left';
  return `${remaining} slot${remaining === 1 ? '' : 's'} left`;
}

/**
 * "the last 30 days", or null while no window is in force.
 *
 * Callers append this to a sentence, so null must drop the whole clause rather
 * than substituting a default — a hard-coded 30 would be a lie between this
 * shipping and the gate flipping.
 */
export function windowPhrase(memoryWindowDays: number | null): string | null {
  if (memoryWindowDays === null || memoryWindowDays <= 0) return null;
  if (memoryWindowDays === 1) return 'the last day';
  return `the last ${memoryWindowDays} days`;
}

/**
 * Active first, then paused, then everyone else — the caller's ordering kept
 * inside each group.
 *
 * Ordering rather than sectioning: the set has to read as one inner circle at the
 * top of the user's own list. The decorate-sort makes stability explicit rather
 * than assumed, since losing the caller's sort inside a group would silently undo
 * it.
 */
export function orderByRemembered<T>(rows: T[]): T[] {
  const rank = (row: T): number => {
    const s = rememberStateOf(row);
    return s === 'active' ? 0 : s === 'paused' ? 1 : 2;
  };
  return rows
    .map((row, i) => ({ row, i, r: rank(row) }))
    .sort((a, b) => (a.r !== b.r ? a.r - b.r : a.i - b.i))
    .map((d) => d.row);
}
